import { ITableRowViewModel } from './tableRowViewModel';
import {
  AgentStepModel,
  AiService,
  StepType,
  LlmProvider,
  AgentClientApplication,
  BaseAgentStepModel,
  InLineControlAiService,
} from '../api/agentStep.model';
import { InLineActivityStepModel } from '../api/inLineActivityStep.model';

export interface InLineActivityStepTableRowViewModel extends ITableRowViewModel {
  id: string;
  integrationId: string;
  zenityAccountId: string;
  threadId: string | null;
  timestamp: string;
  userName: string;
  agentName: string;
  toolDisplayName?: string;
  stepType: string;
  applicationName: string;
  ingestedAt: string;
  schemaVersion: string;
  relatedFindings: string[];
  service?: string;
  transcript?: string;
  params?: string;
  result?: string;
  step: AgentStepModel;
  // ITableRowViewModel members
  rowId: string;
  clickable: boolean;
}

export function transformInLineActivityStepToTableRowViewModel(
  model: InLineActivityStepModel
): InLineActivityStepTableRowViewModel {
  const mappedStep = mapInlineToAgentStep(model);
  return {
    id: model.id,
    integrationId: model.integrationId,
    zenityAccountId: model.zenityAccountId,
    threadId: model.threadId ?? null,
    timestamp: model.timestamp,
    userName: model.userName,
    agentName: model.agentName,
    toolDisplayName: model.toolDisplayName,
    stepType: model.stepType,
    applicationName: model.applicationName,
    ingestedAt: model.ingestedAt,
    schemaVersion: model.schemaVersion,
    relatedFindings: model.relatedFindings,
    service: model.service ?? '',
    transcript: model.transcript,
    params: model.params,
    result: model.result,
    step: mappedStep,
    rowId: model.id,
    clickable: false,
  };
}

function mapInlineToAgentStep(model: InLineActivityStepModel): AgentStepModel {
  const type = normalizeType(model.stepType, model.role);
  const service = inferService(model.service ?? '', model.applicationName);

  type InlineBase = Partial<BaseAgentStepModel> & {
    _inlineMode?: boolean;
    _inlineTranscript?: string;
    triggerIconUrl?: string;
    toolInvocations?: { toolDisplayName?: string; toolOrigin?: string }[];
    attachedDocuments?: unknown[];
    isTranscriptFetchingAllowed?: boolean;
    executionId?: string;
  };

  const base: InlineBase = {
    id: model.id,
    timestamp: new Date(model.timestamp),
    type,
    service: service as AiService,
    integration: { id: model.integrationId },
    zenityAccountId: model.zenityAccountId,
    thread: { id: model.threadId ?? '' },
    mainActor: { displayName: model.userName, id: model.userName },
    relatedActors: [],
    botDetails: {
      displayName: model.agentName || resolveAgentDisplay(model.applicationName),
      llmProvider: LlmProvider.custom,
      modelName: 'N/A',
      platformInfo: { resourceId: '' },
    },
    clientContext: { clientApplication: model.applicationName as unknown as AgentClientApplication },
    indicators: [],
    stepTriggerIds: [],
    _inlineMode: true,
  };

  switch (type) {
    case StepType.userMessageStep:
    case StepType.aiMessageStep:
    case StepType.agentHandoffRequestStep:
    case StepType.agentHandoffResponseStep:
      base.isTranscriptFetchingAllowed = undefined;
      base.attachedDocuments = [];
      if (model.transcript) base._inlineTranscript = model.transcript;
      break;
    case StepType.toolInvocationStep:
      base.toolInvocations = [{ toolDisplayName: model.toolDisplayName || 'Tool', toolOrigin: undefined }];
      base.triggerIconUrl = '';
      if (model.transcript) base._inlineTranscript = model.transcript;
      break;
    case StepType.toolInvocationRequestStep:
      base.toolInvocations = [{ toolDisplayName: model.toolDisplayName || 'Tool', toolOrigin: undefined }];
      if (model.transcript) base._inlineTranscript = model.transcript;
      break;
    case StepType.toolInvocationResultStep:
      base.toolInvocations = [{ toolDisplayName: model.toolDisplayName || 'Tool', toolOrigin: undefined }];
      if (model.transcript) base._inlineTranscript = model.transcript;
      // Try to extract executionId from result JSON if available
      if (model.result) {
        try {
          const resultObj = JSON.parse(model.result);
          if (resultObj?.executionId) {
            base.executionId = resultObj.executionId;
          }
        } catch (err) {
          console.warn('Error parsing result as JSON', err);
        }
      }
      break;
  }

  return base as AgentStepModel;
}

function normalizeType(stepType: string, role?: string): StepType {
  const key = (stepType || '').toLowerCase();
  if (key.includes('toolcallrequeststep')) return StepType.toolInvocationRequestStep;
  if (key.includes('toolcallresultstep')) return StepType.toolInvocationResultStep;
  if (key.includes('toolcallstep')) return StepType.toolInvocationStep;
  if (key.includes('agenthandoffrequest')) return StepType.agentHandoffRequestStep;
  if (key.includes('agenthandoffresponse')) return StepType.agentHandoffResponseStep;
  if (key.includes('user')) return StepType.userMessageStep;
  if (key.includes('ai')) return StepType.aiMessageStep;
  if (key.includes('rag')) return StepType.ragStep;
  if (key.includes('trigger')) return StepType.triggerStep;
  // For generic "messageStep", use role to determine type
  if (key.includes('message')) {
    const roleKey = (role || '').toLowerCase();
    if (roleKey === 'user') return StepType.userMessageStep;
    if (roleKey === 'agent' || roleKey === 'assistant') return StepType.aiMessageStep;
  }
  return StepType.aiMessageStep;
}

function inferService(service: string, applicationName?: string): AiService | InLineControlAiService {
  const s = (service || '').toLowerCase();
  const app = (applicationName || '').toLowerCase();
  if (s === 'customagent' || s === 'custom_agent') return AiService.customAgent;
  if (s.includes('github') || app.includes('github')) return AiService.gitHub;
  if (s.includes('vertex') || app.includes('vertex') || app.includes('gemini')) return AiService.vertexAi;
  if (s.includes('azure') || app.includes('azure')) return AiService.azureAiFoundry;
  if (s.includes('servicenow') || app.includes('servicenow')) return AiService.serviceNow;
  if (s.includes('copilotstudio') || app.includes('copilot studio')) return AiService.copilotStudio;
  if (s.includes('microsoft') || app.includes('copilot')) return AiService.m365Copilot;
  if (s.includes('claude') || app.includes('claude')) return AiService.claude;
  if (s.includes('perplexity') || app.includes('perplexity')) return AiService.perplexity;
  if (s.includes('agentkit') || app.includes('agent kit')) return AiService.agentKit;
  if (s.includes('cursor') || app.includes('cursor')) return AiService.cursor;
  return InLineControlAiService.unknown;
}

function resolveAgentDisplay(applicationName?: string): string {
  if (!applicationName) return 'Unknown';
  const app = applicationName.toLowerCase();
  if (app.includes('github')) return 'GitHub Copilot';
  if (app.includes('copilot')) return 'M365 Copilot';
  if (app.includes('claude')) return 'Claude';
  return applicationName;
}
